import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Dialog from '@material-ui/core/Dialog'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import Input from '@material-ui/core/Input'
import InputLabel from '@material-ui/core/InputLabel'
import Alert from 'ui/components/alert'
import InputAdornment from '@material-ui/core/InputAdornment'
import Typography from '@material-ui/core/Typography'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormHelperText from '@material-ui/core/FormHelperText'
import IconButton from '@material-ui/core/IconButton'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as IconClose } from 'assets/icons/close.svg'
import { ReactComponent as IconEmail } from 'assets/icons/email.svg'
import Grid from '@material-ui/core/Grid'
import useTranslation from 'hooks/useTranslations'
import useStyles from './styles'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'

const ResendInvitation = props => {
  const {
    invitationEmail,
    invitationId,
    isOpen,
    genericError,
    emailError,
    onClose,
    onSubmit,
  } = props

  const t = useTranslation()
  const classes = useStyles()
  const [showEmailError, setShowEmailError] = useState(false)
  const [email, setEmail] = useState(invitationEmail ?? '')
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'))
  const dialogRef = React.useRef(null)

  const handleEmailFocus = () => {
    setShowEmailError(false)
  }

  const handleSubmit = event => {
    event.preventDefault()
    setShowEmailError(true)

    onSubmit({
      invitationId,
      email,
    })
  }

  const handleEmailChange = event => {
    setEmail(event.target.value)
  }

  const isFormDisabled = useMemo(() => {
    return !email
  }, [email])

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      className={clsx(isSmall && classes.dialogXS)}
      fullScreen={isSmall}
    >
      <DialogTitle
        disableTypography
        id="customized-dialog-title"
        className={classes.dialogTitle}
        onClose={onClose}
      >
        <Typography variant="h2">{t('invitation:resend')}</Typography>

        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.dialogClose}
            onClick={onClose}
          >
            <SvgIcon component={IconClose} />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent} ref={dialogRef}>
        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          {genericError ? (
            <Alert extraClass={classes.alert} severity="error">
              {genericError}
            </Alert>
          ) : null}
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel htmlFor="email" shrink>
              {t('vendors:email')}
            </InputLabel>
            <Input
              required
              id="email"
              className={clsx(email && 'hasValue')}
              value={email}
              error={!!(showEmailError && emailError)}
              onFocus={handleEmailFocus}
              onChange={handleEmailChange}
              disableUnderline
              endAdornment={
                <InputAdornment position="end">
                  <SvgIcon component={IconEmail} />
                </InputAdornment>
              }
            />
            {showEmailError && emailError ? (
              <FormHelperText error={true}>
                {t('error:invalid-email')}
              </FormHelperText>
            ) : null}
          </FormControl>
          <Grid container justify="flex-end">
            <Grid item>
              <Button
                type="submit"
                disabled={isFormDisabled}
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {t('invitation:resend')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  )
}

ResendInvitation.propTypes = {
  isOpen: PropTypes.bool,
  genericError: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onResetClick: PropTypes.func,
}

export default ResendInvitation
